import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import { HashMap } from "../../shared/model/HashMap";
import { IElement, IParameters, IParameterSchema } from "../model/IProcessData";
import { ParameterEditorValue } from "../model/ParameterEditorValue";
import { State } from "../model/State";
import { ParameterEditorMode } from "./ParameterInput";
import { ParameterHeaderLabel, ParameterList } from "./ParameterList";
import { Tab, TabList, TabPanel } from "react-tabs";

const ComplexParameter: React.FC<ComplexParameterProps> = (props) => {

    const notTabbedContent = props.subParameters?.filter((p) => p.displayOption !== "Tab");
    const tabbedContent = props.subParameters?.filter((p) => p.displayOption === "Tab");
    const [hoveredTabName, setHoveredTabName] = React.useState<string | undefined>(undefined);

    const handleTabMouseEnter = (tabName: string) => {
        setHoveredTabName(tabName);
    }

    const handleTabMouseLeave = () => {
        setHoveredTabName(undefined);
    }

    if (tabbedContent && tabbedContent.length > 0) {
        return (
            <S.NonSolidTabs
                id="parameter-tab"
                className="custom-tabs"
            >
                <TabList>
                    {tabbedContent?.map((t) => (
                        <Tab
                            key={props.passedKey + t.name + "-header"}
                            id={props.passedKey + t.name + "-header"}
                            onMouseEnter={() => handleTabMouseEnter(t.name)}
                            onMouseLeave={handleTabMouseLeave}
                        >
                            <ParameterHeaderLabel
                                label={t.name}
                                description={t.description}
                                type={t.type}
                                defaultValue={t.defaultValue}
                                shouldNotDisplayIcon={hoveredTabName !== t.name}
                            />
                        </Tab>
                    ))}
                </TabList>
                {tabbedContent?.map((t) =>
                    <TabPanel key={props.passedKey + t.name + "-container"} id={props.passedKey + t.name + "-container"}>
                        <ParameterList
                            key={props.passedKey + t.name}
                            parentName={props.passedKey}
                            argumentJson={t}
                            existingParameters={props.existingParameters}
                            elementId={props.elementId}
                            disabled={props.disabled}
                            onArrayItemAdd={props.onArrayItemAdd}
                            onArrayItemRemove={props.onArrayItemRemove}
                            dontShowHeaderLabel={true}
                            onChangeArrayToExpression={props.onChangeArrayToExpression}
                            onArrayItemIndexChange={props.onArrayItemIndexChange}
                            isForCompletion={props.isForCompletion}
                        />
                    </TabPanel>
                )}
            </S.NonSolidTabs>
        );
    }
    else {
        return (
            <>
                {notTabbedContent?.map((singleArgument) => {
                    return <ParameterList
                        key={props.passedKey + singleArgument.name}
                        parentName={props.passedKey}
                        argumentJson={singleArgument}
                        existingParameters={props.existingParameters}
                        elementId={props.elementId}
                        disabled={props.disabled}
                        onArrayItemAdd={props.onArrayItemAdd}
                        onArrayItemRemove={props.onArrayItemRemove}
                        onChangeArrayToExpression={props.onChangeArrayToExpression}
                        onArrayItemIndexChange={props.onArrayItemIndexChange}
                        isForCompletion={props.isForCompletion}
                    />;
                })}
            </>
        )
    }
};

function getConditionalShowSettings(passedKey: string | undefined, subParameters: IParameterSchema[] | undefined | null, parameters: IParameters): HashMap<IConditionalDisplayTracking> {
    const ret: { [key: string]: IConditionalDisplayTracking } = {};
    if (subParameters) {
        for (const subParameter of subParameters) {
            if (subParameter.conditionalDisplayProperty) {
                const key = getChildKey(subParameter.conditionalDisplayProperty);
                const referencedParameter: ParameterEditorValue = _.get(parameters, key);
                if (referencedParameter == null) {
                    // tslint:disable-next-line:no-console
                    console.log(`Could not find parameter ${key} referenced by conditional display for parameter ${subParameter.name}`);
                    ret[subParameter.name] =
                        {
                            // show by default, unless hidden
                            shouldDisplay: !subParameter.isHidden
                        } as IConditionalDisplayTracking;
                    continue;
                } else {
                    let actualValue = referencedParameter.value as string;

                    let shouldDisplay = false;
                    if (referencedParameter.mode === ParameterEditorMode[ParameterEditorMode.csharp]) {
                        // ACC-7036 Always show the parameter if the referenced value is an expression since it's value is decided at runtime
                        shouldDisplay = true;
                    } else {
                        // ACC-5935 The value of the switch parameter is not stringed like it is in other cases
                        actualValue = actualValue == null ? "" : actualValue.toString();

                        if (subParameter.conditionalDisplayValues?.indexOf(actualValue) !== -1) {
                            shouldDisplay = true;
                        }
                    }

                    ret[subParameter.name] = {
                        shouldDisplay: shouldDisplay
                    } as IConditionalDisplayTracking;
                }
            } else {
                ret[subParameter.name] =
                    {
                        // show by default, unless hidden
                        shouldDisplay: !subParameter.isHidden
                    } as IConditionalDisplayTracking;
            }

        }
    }
    return ret;

    function getChildKey(child: string): string {
        return (passedKey ? passedKey + "." : "") + child;
    }
}

interface IConditionalDisplayTracking {
    shouldDisplay: boolean;
}

interface ComplexParameterProps {
    passedKey?: string;
    elementId?: string;
    argumentJson?: IParameterSchema | null;
    subParameters?: IParameterSchema[];
    existingParameters: IParameters;
    onArrayItemAdd: (key: string, parameters: any) => void;
    onArrayItemRemove: (key: string, index: number) => void;
    onChangeArrayToExpression: (key: string) => void;
    onArrayItemIndexChange: (key: string, oldIndex: number, newIndex: number) => void;
    displaySettings?: HashMap<IConditionalDisplayTracking>;
    taskKey?: string; // Used as react key
    disabled?: boolean;
    isForCompletion?: boolean;
}

function mapStateToProps(state: State, ownProps: ComplexParameterProps): ComplexParameterProps {
    const subParameters =
        ownProps.argumentJson?.subParameters != null ? ownProps.argumentJson.subParameters :
            ownProps.argumentJson?.parameterTemplate != null ? ownProps.argumentJson.parameterTemplate.subParameters :
                null;
    const displaySettings = getConditionalShowSettings(ownProps.passedKey, subParameters,  ownProps.existingParameters);
    const newProps = _.clone(ownProps);
    newProps.subParameters = subParameters?.filter((t) => displaySettings[t.name].shouldDisplay);
    newProps.displaySettings = displaySettings;
    newProps.disabled = ownProps.disabled || state.uiState.disableEdit || state.uiState.isSaving;
    return newProps;
}

export default connect(mapStateToProps)(ComplexParameter);
